import type { PDFSearchResult } from './types';

interface PdfTextItemLike {
  str?: string;
  transform?: number[];
  width?: number;
  height?: number;
}

interface TextItemWithPosition {
  str: string;
  /** Length after whitespace stripping. */
  compactLength: number;
  transform: number[];
  width: number;
  height: number;
  /** Index in the concatenated COMPACT string. */
  charIndex: number;
}

export type PageMatch = Omit<PDFSearchResult, 'page'>;

/**
 * #783: match in whitespace-free space. pdf.js text items can carry
 * internal/trailing spaces (OCR layers, justified text) while the
 * excerpt-derived highlight anchors are compact — stripping whitespace on BOTH
 * sides makes matching symmetric. Multi-word input is searched as a single
 * compact phrase for the same reason (per-word OR turned Latin phrases into
 * stopword noise).
 */
export const toCompactQuery = (query: string): string =>
  query.toLowerCase().replace(/\s+/g, '');

/**
 * #1218: a converted deck emits each list bullet as its own text item, but the
 * PPTX extractor never emits one (bullets live in pPr, not a:t), so an anchor
 * spanning a bullet boundary matched nothing. Only an item that is ENTIRELY
 * markers is dropped — '≤' is also a standalone item on real decks and does
 * appear in the extracted text, and a bullet glyph inside body text keeps its
 * item because that item still carries words.
 */
const LIST_MARKERS = /^[•‣⁃▪▫●○◦■□◆◇➢➤・·]+$/;

interface ItemBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  fontSize: number;
}

/**
 * #1304: one merged box per match painted the whole gap between two shapes on a
 * converted slide (112pt of a 405pt slide on a real deck). Matched items are
 * grouped by baseline instead, so each line gets its own tight rectangle — the
 * same thing pdf.js does. Japanese PDFs extract one character per item, so items
 * sharing a baseline must still merge into a single rect, not one per character.
 */
const groupByLine = (items: ItemBox[]): Array<[number, number, number, number]> => {
  // Line spacing is at least the font size, so half of it separates lines
  // without splitting the sub-point baseline jitter within one line.
  const sameLine = (a: ItemBox, b: ItemBox) =>
    Math.abs(a.minY - b.minY) <= Math.max(1, Math.min(a.fontSize, b.fontSize) / 2);

  const lines: ItemBox[][] = [];
  for (const item of [...items].sort((a, b) => b.minY - a.minY)) {
    const current = lines[lines.length - 1];
    if (current && sameLine(current[0], item)) current.push(item);
    else lines.push([item]);
  }
  return lines.map(line => [
    Math.min(...line.map(i => i.minX)),
    Math.min(...line.map(i => i.minY)),
    Math.max(...line.map(i => i.maxX)),
    Math.max(...line.map(i => i.maxY)),
  ]);
};

const unionOf = (
  rects: Array<[number, number, number, number]>,
): [number, number, number, number] => [
  Math.min(...rects.map(r => r[0])),
  Math.min(...rects.map(r => r[1])),
  Math.max(...rects.map(r => r[2])),
  Math.max(...rects.map(r => r[3])),
];

/**
 * Japanese PDFs often extract as individual characters, so matches are found in
 * the page's concatenated text and then mapped back to the items that overlap
 * them, grouped into one bounding box per line.
 */
export function collectPageMatches(items: readonly unknown[], term: string): PageMatch[] {
  if (!term) return [];

  const textItems: TextItemWithPosition[] = [];
  let fullText = '';

  for (const raw of items) {
    const item = raw as PdfTextItemLike;
    if (typeof item?.str !== 'string' || !item.str) continue;

    const compactStr = item.str.replace(/\s+/g, '');
    if (LIST_MARKERS.test(compactStr)) continue;
    textItems.push({
      str: item.str,
      compactLength: compactStr.length,
      transform: item.transform ?? [],
      width: item.width ?? 0,
      height: item.height || 0,
      charIndex: fullText.length,
    });
    fullText += compactStr;
  }

  const fullTextLower = fullText.toLowerCase();
  const matches: PageMatch[] = [];

  let searchIndex = 0;
  let matchIndex: number;
  while ((matchIndex = fullTextLower.indexOf(term, searchIndex)) !== -1) {
    const matchEnd = matchIndex + term.length;

    const matched: ItemBox[] = [];

    for (const textItem of textItems) {
      const itemStart = textItem.charIndex;
      // Whitespace-only items have compactLength 0 and can never overlap a
      // match (itemStart === itemEnd).
      const itemEnd = itemStart + textItem.compactLength;

      if (itemStart < matchEnd && itemEnd > matchIndex) {
        const transform = textItem.transform;
        const fontSize = Math.sqrt(transform[0] * transform[0] + transform[1] * transform[1]);

        const pdfX = transform[4];
        const pdfY = transform[5];
        const pdfW = textItem.width || fontSize * textItem.str.length * 0.6;

        matched.push({ minX: pdfX, minY: pdfY, maxX: pdfX + pdfW, maxY: pdfY + fontSize, fontSize });
      }
    }

    if (matched.length > 0) {
      const pdfRects = groupByLine(matched);
      const pdfRect = unionOf(pdfRects);

      const isDuplicate = matches.some(m =>
        Math.abs(m.pdfRect[0] - pdfRect[0]) < 1 &&
        Math.abs(m.pdfRect[1] - pdfRect[1]) < 1
      );

      if (!isDuplicate) {
        matches.push({ pdfRect, pdfRects, text: fullText.substring(matchIndex, matchEnd) });
      }
    }

    searchIndex = matchIndex + 1;
  }

  return matches;
}

/**
 * The result nearest the page the excerpt came from, breaking a tie in favour
 * of the later page — an excerpt's own text usually starts at or after the
 * page the backend reported.
 */
export function pickPreferredIndex(
  results: PDFSearchResult[],
  preferredPage: number | undefined,
): number {
  if (!preferredPage || preferredPage <= 0) return 0;

  return results.reduce((bestIndex, result, index) => {
    const best = results[bestIndex];
    const distance = Math.abs(result.page - preferredPage);
    const bestDistance = Math.abs(best.page - preferredPage);
    if (distance < bestDistance) return index;
    if (
      distance === bestDistance &&
      result.page >= preferredPage &&
      best.page < preferredPage
    ) {
      return index;
    }
    return bestIndex;
  }, 0);
}
